import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import en from '../i18n/en.json';
import fr from '../i18n/fr.json';
import es from '../i18n/es.json';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const translations = { en, fr, es };
const SUPPORTED_LANGS = ['en', 'fr', 'es'];

// Module-level cache: prevents React StrictMode double-fire from consuming multiple logins.
// Also prevents page refresh from re-validating (which would exhaust demo links).
const _showroomCache = {};

const detectLang = (search) => {
  const params = new URLSearchParams(search);
  const qLang = params.get('lang');
  if (SUPPORTED_LANGS.includes(qLang)) return qLang;
  const browserLang = (navigator.language || '').slice(0, 2);
  if (SUPPORTED_LANGS.includes(browserLang)) return browserLang;
  return 'en';
};

const tShowroom = (key, lang) => {
  const keys = key.split('.');
  let val = translations[lang] || translations.en;
  for (const k of keys) {
    if (val && typeof val === 'object' && k in val) val = val[k];
    else {
      let fb = translations.en;
      for (const fk of keys) { if (fb && typeof fb === 'object' && fk in fb) fb = fb[fk]; else return key; }
      return fb;
    }
  }
  return val;
};

const displayI18n = (v, lang = 'en') => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v[lang] || v.en || Object.values(v).find(x => x) || '';
};

const LANG_LABELS = { en: 'EN', fr: 'FR', es: 'ES' };

// Derive a short card identifier from its full label
// "Guest Welcome" → "WELCOME", "Premium Wi-Fi" → "WI-FI", "Restaurant" → "RESTAURANT"
const shortCardLabel = (label, lang) => {
  const full = displayI18n(label, lang) || '';
  const words = full.trim().split(/\s+/);
  if (words.length <= 2) return full.toUpperCase();
  return words[words.length - 1].toUpperCase();
};

// Per-card spotlight menu for multi-card experiences
const CardSpotlightMenu = ({ card, cardIndex, lang, isActive, onSelect, selectedKey, onModeChange, getActionLabel, actualCardHeight, mobileMode, socialMediaConfig, onSocialMediaChange, activeSocialMedia }) => {
  const actions = Object.entries(card.destinations || {});
  const scale = card.card_scale || 1;
  const label = shortCardLabel(card.label, lang);

  // Position menu below the card using actual measured height when available
  const baseW = 240;
  const cardW = baseW * scale;
  const halfCardH = actualCardHeight
    ? Math.round(actualCardHeight / 2)
    : Math.round(((cardW * 1.2) + 56) / 2);
  const menuOffsetY = halfCardH + 16;

  // Menu width matches the visual card width at this scale
  const hasSocial = socialMediaConfig && socialMediaConfig.length > 0;
  const tapH = card.card_appearance === 'invisible' && card.tap_area_width > 0 && card.tap_area_height > 0 ? card.tap_area_height : null;
  const menuW = tapH !== null
    ? (hasSocial ? 340 : 260)
    : Math.max(hasSocial ? 340 : 260, Math.round(cardW * (hasSocial ? 1.3 : 0.95)));

  // Use the active social media label if set, otherwise fall back to first match for this card
  const currentSocialKey = activeSocialMedia || socialMediaConfig?.find(sm => sm.card_id === card.id)?.label || '';

  const menuContent = (
    <div onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={`flex items-center justify-between gap-2 sm:gap-3 rounded-xl cursor-pointer
        px-2.5 sm:px-3.5 py-1.5 sm:py-2 border transition-all duration-300
        ${isActive
          ? 'border-[#0077FF]/25 shadow-[0_0_20px_rgba(0,119,255,0.12)]'
          : 'border-white/[0.06] hover:border-white/[0.12]'
        }`}
      style={{ background: 'rgba(8,14,28,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', width: mobileMode ? '100%' : `${menuW}px` }}>

      <span className="text-[7px] sm:text-[8px] font-bold tracking-[0.12em] text-white/30 uppercase whitespace-nowrap select-none">
        DEMO MODE
      </span>

      {actions.length > 0 && (
        <div className="relative flex-1">
          <select value={selectedKey}
            onChange={(e) => { e.stopPropagation(); onModeChange(e.target.value); }}
            className="w-full appearance-none bg-white/[0.07] border border-white/[0.1] rounded-lg
              pl-2.5 pr-6 py-1 text-[9px] sm:text-[10px] font-semibold text-white/80
              tracking-wider cursor-pointer focus:outline-none focus:border-[#0077FF]/40
              hover:bg-white/[0.1] transition-colors"
            data-testid={`spotlight-dropdown-${cardIndex}`}>
            {actions.map(([key]) => (
              <option key={key} value={key} className="bg-[#0c1628] text-white">
                {getActionLabel(key)}
              </option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      )}

      {actions.length === 0 && (
        <span className="text-[8px] text-white/20 tracking-wider uppercase italic">No actions</span>
      )}

      {hasSocial && (
        <>
          <div className="w-px h-3.5 bg-white/[0.08]" />
          <div className="relative flex-1">
            <select value={currentSocialKey}
              onChange={(e) => { e.stopPropagation(); onSocialMediaChange(e.target.value); }}
              className="w-full appearance-none bg-white/[0.07] border border-white/[0.1] rounded-lg
                pl-2.5 pr-6 py-1 text-[9px] sm:text-[10px] font-semibold text-white/80
                tracking-wider cursor-pointer focus:outline-none focus:border-[#0077FF]/40
                hover:bg-white/[0.1] transition-colors"
              data-testid={`social-media-dropdown-${cardIndex}`}>
              {socialMediaConfig.map(sm => (
                <option key={sm.label} value={sm.label} className="bg-[#0c1628] text-white">
                  {sm.label}
                </option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </div>
        </>
      )}

      <div className="flex items-center gap-1 ml-0.5">
        <div className={`w-1 h-1 rounded-full ${isActive ? 'bg-[#0077FF]' : 'bg-white/20'}`} />
        <span className={`text-[7px] sm:text-[8px] font-bold tracking-[0.15em] uppercase whitespace-nowrap select-none
          ${isActive ? 'text-white/50' : 'text-white/25'}`}>
          {label}
        </span>
      </div>
    </div>
  );

  if (mobileMode) {
    return <div data-testid={`spotlight-menu-${cardIndex}`}>{menuContent}</div>;
  }

  // Invisible cards: sit just under the tap area (not the scale-based card size), kept on screen
  const position = tapH !== null
    ? { top: `min(calc(${card.position_y + tapH / 2}% + 8px), calc(100% - 56px))`, transform: 'translate(-50%, 0)' }
    : { top: `${card.position_y}%`, transform: `translate(-50%, ${menuOffsetY}px)` };

  return (
    <div className="absolute" data-testid={`spotlight-menu-${cardIndex}`}
      style={{
        left: `${card.position_x}%`, ...position,
        zIndex: isActive ? 18 : 12,
        transition: 'opacity 0.5s ease',
      }}>
      {menuContent}
    </div>
  );
};

const TapCard = ({ card, index, lang, isActive, onSelect, onTap, selectedDestUrl, syncedImgHeight, onImageLoad, mobileMode, demoMode }) => {
  const [tapped, setTapped] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [card.card_image_url]);
  const scale = card.card_scale || 1;
  const baseWidth = 200;
  const smWidth = 240;
  const w = Math.round(baseWidth * scale);
  const smW = Math.round(smWidth * scale);

  const appearance = card.card_appearance || '';
  const isHotspot = appearance === 'hotspot';
  const isInvisible = appearance === 'invisible';
  const hasCustomArt = !isHotspot && !isInvisible && !!card.card_image_url && !imgFailed;

  // Fixed thin padding at ALL scales — border thickness never changes
  const pad = hasCustomArt ? 0 : 8;
  const smPad = hasCustomArt ? 0 : 10;

  const handleTap = useCallback(() => {
    onSelect();
    // Action cards: delegate to parent onTap, no navigation
    if (card.card_type === 'action') {
      if (onTap) onTap();
      setTapped(true);
      setTimeout(() => setTapped(false), 450);
      return;
    }
    const url = selectedDestUrl || (() => {
      const dests = Object.entries(card.destinations || {});
      return dests.length === 1 ? dests[0][1] : null;
    })();
    if (url) {
      // When Demo Mode is ON, signal external destinations via query param
      const finalUrl = demoMode ? (() => {
        try {
          const u = new URL(url, window.location.origin);
          u.searchParams.set('tapelligence_demo', '1');
          return u.toString();
        } catch { return url; }
      })() : url;
      if (onTap) onTap(finalUrl);
      setTapped(true);
      setTimeout(() => {
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
        setTapped(false);
      }, 450);
    }
  }, [card.destinations, card.card_type, onSelect, onTap, selectedDestUrl, demoMode]);

  const resolveImg = (url) => {
    if (!url) return '/yulontap_card_logo.png?v=2';
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  // ── Hotspot / Invisible appearance ──
  if (isHotspot || isInvisible) {
    const dotSize = Math.max(44, Math.round(24 * scale));
    const hasTapArea = (card.tap_area_width || 0) > 0 && (card.tap_area_height || 0) > 0;
    return (
      <button onClick={handleTap} data-testid={`tap-card-${card.id}`}
        className={`${mobileMode ? 'relative' : 'absolute'} group cursor-pointer focus:outline-none flex items-center justify-center`}
        style={mobileMode ? {
          animation: `showroom-enter-mobile 0.8s ease-out forwards`,
          animationDelay: `${0.4 + index * 0.2}s`,
          opacity: 0,
          zIndex: isActive ? 10 : 1,
          width: hasTapArea ? `${card.tap_area_width}%` : undefined,
          height: hasTapArea ? `${card.tap_area_height}%` : undefined,
          minWidth: '44px', minHeight: '44px',
        } : {
          left: `${card.position_x}%`, top: `${card.position_y}%`,
          transform: 'translate(-50%, -50%)',
          animation: `showroom-enter 0.8s ease-out forwards`,
          animationDelay: `${0.4 + index * 0.2}s`,
          opacity: 0,
          zIndex: isActive ? 10 : 1,
          width: hasTapArea ? `${card.tap_area_width}%` : undefined,
          height: hasTapArea ? `${card.tap_area_height}%` : undefined,
          minWidth: '44px', minHeight: '44px',
        }}>
        {isHotspot && (
          <div className="relative flex items-center justify-center" style={{ width: `${dotSize}px`, height: `${dotSize}px` }}>
            <div className="absolute rounded-full bg-[#0077FF]/20" style={{
              width: `${dotSize}px`, height: `${dotSize}px`,
              animation: 'hotspotPulse 2s ease-in-out infinite',
            }} />
            <div className={`relative rounded-full bg-[#0077FF] shadow-[0_0_12px_rgba(0,119,255,0.6)] transition-transform duration-200 ${tapped ? 'scale-75' : isActive ? 'scale-110' : ''}`}
              style={{ width: `${Math.max(12, Math.round(12 * scale))}px`, height: `${Math.max(12, Math.round(12 * scale))}px` }} />
            <style>{`@keyframes hotspotPulse { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.6); opacity: 0; } }`}</style>
          </div>
        )}
        {isInvisible && (
          <div style={{ width: '100%', height: '100%', minWidth: '44px', minHeight: '44px' }} />
        )}
      </button>
    );
  }

  return (
    <button onClick={handleTap} data-testid={`tap-card-${card.id}`}
      className={`${mobileMode ? 'relative' : 'absolute'} group cursor-pointer focus:outline-none`}
      style={mobileMode ? {
        animation: `showroom-enter-mobile 0.8s ease-out forwards`,
        animationDelay: `${0.4 + index * 0.2}s`,
        opacity: 0,
        zIndex: isActive ? 10 : 1,
      } : {
        left: `${card.position_x}%`, top: `${card.position_y}%`,
        transform: 'translate(-50%, -50%)',
        animation: `showroom-enter 0.8s ease-out forwards`,
        animationDelay: `${0.4 + index * 0.2}s`,
        opacity: 0,
        zIndex: isActive ? 10 : 1,
      }}>
      <div className={`relative transition-all duration-400 ease-out
        ${hasCustomArt ? '' : 'rounded-2xl overflow-hidden'}
        ${hasCustomArt
          ? (tapped ? 'scale-[0.96]' : isActive ? 'scale-[1.04]' : 'group-hover:scale-[1.06] group-hover:-translate-y-1.5')
          : (tapped ? 'scale-[0.96] shadow-[0_0_60px_rgba(0,119,255,0.5)]' :
             isActive ? 'scale-[1.04] shadow-[0_24px_70px_rgba(0,119,255,0.3)] ring-2 ring-[#0077FF]/40' :
             'group-hover:scale-[1.06] group-hover:-translate-y-1.5 shadow-[0_12px_50px_rgba(0,0,0,0.6)] group-hover:shadow-[0_24px_70px_rgba(0,0,0,0.7)]')
        }`}
        style={{
          width: `${w}px`,
          ...(hasCustomArt ? {} : { aspectRatio: '3/2', background: 'linear-gradient(145deg, #0c1222 0%, #162032 50%, #0f1a2e 100%)' }),
          border: hasCustomArt ? 'none' : `1px solid ${isActive ? 'rgba(0,119,255,0.25)' : 'rgba(255,255,255,0.07)'}`,
          ...(hasCustomArt ? {
            filter: tapped
              ? 'drop-shadow(0 0 25px rgba(0,119,255,0.5))'
              : isActive
                ? 'drop-shadow(0 0 12px rgba(0,119,255,0.45)) drop-shadow(0 0 50px rgba(0,119,255,0.15))'
                : undefined,
          } : {}),
        }}>
        <style>{`
          @media (min-width: 640px) { [data-testid="tap-card-${card.id}"] > div { width: ${smW}px !important; } [data-testid="tap-card-${card.id}"] .tap-card-img-area { padding: ${smPad}px !important; } }
          ${hasCustomArt ? `[data-testid="tap-card-${card.id}"] > div { filter: drop-shadow(0 10px 25px rgba(0,0,0,0.6)); transition: filter 0.4s ease-out, transform 0.4s ease-out; }
          [data-testid="tap-card-${card.id}"]:hover > div { filter: drop-shadow(0 20px 35px rgba(0,0,0,0.7)); }` : ''}
        `}</style>
        <div className="tap-card-img-area flex items-center justify-center"
          style={{ padding: `${pad}px`, minHeight: (syncedImgHeight && hasCustomArt) ? `${syncedImgHeight + pad * 2}px` : undefined }}>
          <img src={imgFailed ? '/yulontap_card_logo.png?v=2' : resolveImg(card.card_image_url)}
            alt={displayI18n(card.label, lang) || 'Tap Card'}
            className={`select-none pointer-events-none rounded-lg ${hasCustomArt ? 'w-full h-auto' : 'w-full h-full object-contain'}`}
            style={hasCustomArt ? {} : { filter: 'brightness(1.1)', maxWidth: '70%' }} draggable={false}
            onLoad={(e) => {
              if (onImageLoad) {
                onImageLoad(card.id, e.target.naturalWidth, e.target.naturalHeight);
              }
            }}
            onError={() => {
              if (card.card_image_url) setImgFailed(true);
              // Record fallback aspect ratio so this card contributes to multi-card height sync
              if (onImageLoad) onImageLoad(card.id, 3, 2);
            }} />
        </div>
        {!hasCustomArt && (
          <div className="px-4 pb-3 sm:pb-3.5">
            <div className="flex items-center gap-1.5">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <span className="text-[7px] sm:text-[8px] font-bold tracking-[0.3em] text-[#0077FF]/70 group-hover:text-[#0077FF] uppercase transition-colors"
                style={{ fontSize: `${Math.max(7, Math.round(8 * Math.min(scale, 1.5)))}px` }}>
                {tShowroom('showroom.tapToExplore', lang)}
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>
          </div>
        )}
        {tapped && (
          <div className={`absolute inset-0 ${hasCustomArt ? '' : 'rounded-2xl'} animate-pulse`} style={{
            background: 'radial-gradient(circle at center, rgba(0,119,255,0.15) 0%, transparent 70%)',
            border: hasCustomArt ? 'none' : '2px solid rgba(0,119,255,0.3)',
          }} />
        )}
      </div>
    </button>
  );
};

// Full-page passcode entry screen for passcode-protected showcase links
const PasscodeScreen = ({ onSubmit, error, loading, expired, expiredMessage }) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => { if (!expired) refs[0].current?.focus(); }, [expired]);

  const handleChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 3) refs[idx + 1].current?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs[idx - 1].current?.focus();
    }
    if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === 4 && !loading) onSubmit(code);
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (text.length === 4) {
      setDigits(text.split(''));
      refs[3].current?.focus();
      e.preventDefault();
    }
  };

  const code = digits.join('');
  const canSubmit = code.length === 4 && !loading;

  if (expired) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(145deg, #060a14 0%, #0c1628 50%, #060a14 100%)' }} data-testid="passcode-expired-screen">
        <div className="text-center max-w-sm px-6">
          <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-10 mx-auto mb-8" loading="eager" />
          <div className="w-16 h-16 rounded-2xl bg-red-500/[0.08] border border-red-500/20 flex items-center justify-center mx-auto mb-5">
            <svg className="w-7 h-7 text-red-400/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-white/90 mb-2" data-testid="expired-title">Showcase Expired</h1>
          <p className="text-white/40 text-sm leading-relaxed">{expiredMessage || 'This showcase link is no longer active. Please request a new link from the sender.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(145deg, #060a14 0%, #0c1628 50%, #060a14 100%)' }} data-testid="passcode-screen">
      <div className="text-center max-w-sm px-6 w-full">
        <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-10 mx-auto mb-8" loading="eager" />
        <h1 className="text-xl font-semibold text-white/90 mb-2">Enter Passcode</h1>
        <p className="text-white/35 text-sm mb-8">Enter the 4-digit passcode to access this showcase</p>

        <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={refs[i]}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              autoComplete="off"
              data-testid={`passcode-digit-${i}`}
              className={`w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 bg-white/[0.04] text-white
                focus:outline-none focus:border-[#0077FF] focus:ring-2 focus:ring-[#0077FF]/20 transition-all
                ${error ? 'border-red-500/50 animate-shake' : 'border-white/10'}`}
            />
          ))}
        </div>

        {error && (
          <p className="text-red-400 text-sm mb-4" data-testid="passcode-error">{error}</p>
        )}

        <button
          onClick={() => canSubmit && onSubmit(code)}
          disabled={!canSubmit}
          data-testid="passcode-submit-btn"
          className={`w-full max-w-[250px] py-3 rounded-xl text-sm font-semibold transition-all
            ${canSubmit
              ? 'bg-[#0077FF] hover:bg-[#0066DD] text-white cursor-pointer'
              : 'bg-white/[0.06] text-white/20 cursor-not-allowed'}`}
        >
          {loading ? 'Verifying...' : 'Continue'}
        </button>
      </div>
      <style>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-6px)} 40%,80%{transform:translateX(6px)} }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
};

const ShowroomPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => detectLang(location.search));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [experience, setExperience] = useState(null);
  const [allowedModes, setAllowedModes] = useState([]);
  const [modeLabels, setModeLabels] = useState({});
  const [activeSceneIndex, setActiveSceneIndex] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [entered, setEntered] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [cardModes, setCardModes] = useState({}); // { cardId: selectedActionKey }
  const [activeSocialMedia, setActiveSocialMedia] = useState(null); // tracks selected social media label
  const [cardImageDims, setCardImageDims] = useState({}); // { cardId: { w, h } }
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  const [fullShowroomGallery, setFullShowroomGallery] = useState(null); // list of experiences for full_showroom/showroom tokens
  const [fullShowroomSessionId, setFullShowroomSessionId] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [demoMode, setDemoMode] = useState(false);
  const [showroomName, setShowroomName] = useState(null);
  const [passcodeRequired, setPasscodeRequired] = useState(false);
  const [passcodeError, setPasscodeError] = useState('');
  const [passcodeLoading, setPasscodeLoading] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');

  // ── Action Card (GOAL flash) state ──
  const [goalFlash, setGoalFlash] = useState(false);
  const goalCooldownRef = useRef(false);
  const goalAudioRef = useRef(null);
  const validatedPasscodeRef = useRef(null);

  const isPreview = location.pathname.startsWith('/experience/admin-preview/');
  const isTheShow = location.pathname.startsWith('/the-show/');
  const isDemonstrate = isPreview && new URLSearchParams(location.search).get('mode') === 'demonstrate';
  const rawToken = params.token || params.experienceId || '';
  const token = rawToken.split('\n')[0].split('%0A')[0].split('%0a')[0].trim();
  const t = (key) => tShowroom(key, lang);

  // ── Analytics: fire-and-forget tap recording ──
  const recordTap = useCallback((card, destKey, destUrl) => {
    const scene = experience?.scenes?.[activeSceneIndex];
    const payload = {
      experience_id: experience?.id || '',
      scene_id: scene?.id || '',
      instance_id: card.id || '',
      capability_id: card.capability_id || '',
      card_label: displayI18n(card.label, lang),
      destination_key: destKey,
      destination_url: destUrl,
      session_id: sessionId || fullShowroomSessionId || '',
    };
    fetch(`${API_URL}/api/analytics/tap`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, [experience, activeSceneIndex, lang, sessionId, fullShowroomSessionId]);

  // ── Action Card tap handler (GOAL flash + horn + silent POST) ──
  const handleActionTap = useCallback((card) => {
    if (goalCooldownRef.current) return; // ignore within 2s
    goalCooldownRef.current = true;
    setTimeout(() => { goalCooldownRef.current = false; }, 2000);

    // 1. Fire silent POST (no-cors, fire-and-forget)
    if (card.trigger_url) {
      fetch(card.trigger_url, { method: 'POST', mode: 'no-cors' }).catch(() => {});
    }

    // 2. Play goal horn
    const audioSrc = card.audio_url
      ? (card.audio_url.startsWith('http') ? card.audio_url : `${API_URL}${card.audio_url}`)
      : null;
    if (audioSrc) {
      try {
        if (goalAudioRef.current) { goalAudioRef.current.pause(); goalAudioRef.current.currentTime = 0; }
        const audio = new Audio(audioSrc);
        goalAudioRef.current = audio;
        audio.play().catch(() => {});
      } catch (_) {}
    }

    // 3. Show red GOAL flash
    setGoalFlash(true);
    setTimeout(() => setGoalFlash(false), 1500);
  }, []);

  // ── Passcode submission handler ──
  const handlePasscodeSubmit = useCallback(async (code) => {
    setPasscodeError('');
    setPasscodeLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/showroom/validate?lang=${lang}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, passcode: code })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.detail && (data.detail.includes('expired') || data.detail.includes('Expired'))) {
          setPasscodeRequired(false);
          setLinkExpired(true);
          setExpiredMessage(data.detail);
          return;
        }
        setPasscodeError(data.detail === 'Invalid passcode' ? 'Incorrect passcode. Please try again.' : (data.detail || 'Access denied'));
        return;
      }
      const data = await res.json();
      // Cache successful validation
      _showroomCache[token] = data;
      try { sessionStorage.setItem(`showroom_${token}`, JSON.stringify(data)); } catch (_e) {}
      // Store passcode for subsequent gallery experience loading
      validatedPasscodeRef.current = code;
      // Set state
      if ((data.scope === 'full_showroom' || data.scope === 'showroom') && data.experiences && !data.experience) {
        setFullShowroomGallery(data.experiences);
        setFullShowroomSessionId(data.session_id);
        if (data.showroom_name) setShowroomName(data.showroom_name);
      } else {
        setExperience(data.experience);
        setAllowedModes(data.allowed_modes);
        setModeLabels(data.mode_labels);
        setSessionId(data.session_id || '');
        if (data.session_timer) setSessionTimer(data.session_timer);
        if (data.demo_mode) setDemoMode(true);
      }
      setPasscodeRequired(false);
      setEntered(false);
      setTimeout(() => setEntered(true), 200);
    } catch (_err) {
      setPasscodeError('Connection error. Please try again.');
    } finally {
      setPasscodeLoading(false);
    }
  }, [token, lang]);

  useEffect(() => {
    // For public tokens, check module-level cache first to prevent
    // StrictMode double-fire and page-refresh from consuming extra logins.
    if (!isPreview && !isTheShow && _showroomCache[token] && _showroomCache[token] !== 'pending') {
      const data = _showroomCache[token];
      if ((data.scope === 'full_showroom' || data.scope === 'showroom') && data.experiences && !data.experience) {
        setFullShowroomGallery(data.experiences);
        setFullShowroomSessionId(data.session_id);
        if (data.showroom_name) setShowroomName(data.showroom_name);
      } else {
        setExperience(data.experience);
        setAllowedModes(data.allowed_modes);
        setModeLabels(data.mode_labels);
        setSessionId(data.session_id || '');
        if (data.session_timer) setSessionTimer(data.session_timer);
        if (data.demo_mode) setDemoMode(true);
      }
      setLoading(false);
      setTimeout(() => setEntered(true), 150);
      return;
    }

    // Also check sessionStorage (survives hot-reload but not tab close)
    if (!isPreview && !isTheShow) {
      try {
        const cached = sessionStorage.getItem(`showroom_${token}`);
        if (cached) {
          const data = JSON.parse(cached);
          _showroomCache[token] = data;
          if ((data.scope === 'full_showroom' || data.scope === 'showroom') && data.experiences && !data.experience) {
            setFullShowroomGallery(data.experiences);
            setFullShowroomSessionId(data.session_id);
            if (data.showroom_name) setShowroomName(data.showroom_name);
          } else {
            setExperience(data.experience);
            setAllowedModes(data.allowed_modes);
            setModeLabels(data.mode_labels);
            setSessionId(data.session_id || '');
            if (data.session_timer) setSessionTimer(data.session_timer);
            if (data.demo_mode) setDemoMode(true);
          }
          setLoading(false);
          setTimeout(() => setEntered(true), 150);
          return;
        }
      } catch (_e) { /* ignore parse errors */ }
    }

    // If already in-flight from StrictMode first-mount, skip entirely.
    // The first mount's async callback will still set state on this component.
    if (!isPreview && !isTheShow && _showroomCache[token] === 'pending') return;

    // Mark as in-flight to block StrictMode re-fire
    if (!isPreview && !isTheShow) _showroomCache[token] = 'pending';

    const validate = async () => {
      try {
        let endpoint, options;
        if (isPreview) {
          endpoint = `${API_URL}/api/showroom/admin-preview/${token}?lang=${lang}`;
          options = { method: 'GET', credentials: 'include' };
        } else if (isTheShow) {
          endpoint = `${API_URL}/api/showroom/the-show/${token}?lang=${lang}`;
          options = { method: 'GET', credentials: 'include' };
        } else {
          endpoint = `${API_URL}/api/showroom/validate?lang=${lang}`;
          options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token })
          };
        }
        const res = await fetch(endpoint, options);
        if (!res.ok) {
          if (isTheShow && res.status === 401) {
            navigate(`/admin/login?redirect=/the-show/${token}`, { replace: true });
            return;
          }
          const data = await res.json().catch(() => ({}));
          // Passcode-protected link: show passcode entry screen
          if (data.detail === 'passcode_required') {
            if (!isPreview && !isTheShow) delete _showroomCache[token];
            setPasscodeRequired(true);
            return;
          }
          // Expired links: show expired screen
          if (data.detail && (data.detail.includes('expired') || data.detail.includes('Expired'))) {
            setLinkExpired(true);
            setExpiredMessage(data.detail);
            return;
          }
          throw new Error(data.detail || 'Access denied');
        }
        const data = await res.json();
        // Cache successful validation
        if (!isPreview && !isTheShow) {
          _showroomCache[token] = data;
          try { sessionStorage.setItem(`showroom_${token}`, JSON.stringify(data)); } catch (_e) { /* ignore */ }
        }

        // Handle full_showroom scope → show gallery picker
        if ((data.scope === 'full_showroom' || data.scope === 'showroom') && data.experiences && !data.experience) {
          setFullShowroomGallery(data.experiences);
          setFullShowroomSessionId(data.session_id);
          if (data.showroom_name) setShowroomName(data.showroom_name);
        } else {
          setExperience(data.experience);
          setAllowedModes(data.allowed_modes);
          setModeLabels(data.mode_labels);
          setSessionId(data.session_id || '');
          if (data.session_timer) setSessionTimer(data.session_timer);
          if (data.demo_mode) setDemoMode(true);
        }
      } catch (err) {
        // If we got an error but there's cached data (from a prior successful call), use it
        if (!isPreview && !isTheShow && _showroomCache[token] && _showroomCache[token] !== 'pending') {
          const data = _showroomCache[token];
          if ((data.scope === 'full_showroom' || data.scope === 'showroom') && data.experiences && !data.experience) {
            setFullShowroomGallery(data.experiences);
            setFullShowroomSessionId(data.session_id);
          } else {
            if (data.showroom_name) setShowroomName(data.showroom_name);
            setExperience(data.experience);
            setAllowedModes(data.allowed_modes);
            setModeLabels(data.mode_labels);
            setSessionId(data.session_id || '');
            if (data.session_timer) setSessionTimer(data.session_timer);
            if (data.demo_mode) setDemoMode(true);
          }
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
        setTimeout(() => setEntered(true), 150);
      }
    };
    validate();
  }, [token, isPreview, isTheShow, lang]);

  const changeLang = (newLang) => {
    setLang(newLang);
    const url = new URL(window.location);
    url.searchParams.set('lang', newLang);
    window.history.replaceState({}, '', url);
  };

  // Load a specific experience from full-showroom gallery
  const loadGalleryExperience = useCallback(async (experienceId) => {
    try {
      setLoading(true);
      setError(null);
      const payload = { token, experience_id: experienceId };
      if (validatedPasscodeRef.current) payload.passcode = validatedPasscodeRef.current;
      const res = await fetch(`${API_URL}/api/showroom/validate?lang=${lang}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load experience');
      }
      const data = await res.json();
      setExperience(data.experience);
      setAllowedModes(data.allowed_modes || []);
      setModeLabels(data.mode_labels || {});
      setActiveSceneIndex(0);
      setActiveCardIndex(0);
      setBgLoaded(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setEntered(true), 150);
    }
  }, [token, lang]);

  // Return to gallery from a loaded experience
  const backToGallery = useCallback(() => {
    setExperience(null);
    setAllowedModes([]);
    setModeLabels({});
    setActiveSceneIndex(0);
    setActiveCardIndex(0);
    setBgLoaded(false);
  }, []);

  // Reset card selection and image dims when scene changes
  useEffect(() => {
    setActiveCardIndex(0);
    setCardImageDims({});
  }, [activeSceneIndex]);

  useEffect(() => {
    if (!sessionTimer?.started_at || !sessionTimer?.duration_minutes) return;
    const endTime = new Date(sessionTimer.started_at).getTime() + sessionTimer.duration_minutes * 60 * 1000;
    const tick = () => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) setError('Demo session has expired');
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [sessionTimer]);

  // Track image dimensions for uniform card sizing in multi-card scenes
  const handleCardImageLoad = useCallback((cardId, naturalWidth, naturalHeight) => {
    setCardImageDims(prev => ({ ...prev, [cardId]: { w: naturalWidth, h: naturalHeight } }));
  }, []);

  // Track mobile viewport
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Social media config from backend — drives the second dropdown
  const socialMediaConfig = experience?.social_media_config || [];

  // Handle social media selection: switch to the card mapped to the selected platform
  const handleSocialMediaChange = useCallback((label) => {
    const smConfig = experience?.social_media_config || [];
    const option = smConfig.find(sm => sm.label === label);
    if (!option) return;
    setActiveSocialMedia(label);
    const scenes = experience?.scenes || [];
    for (let si = 0; si < scenes.length; si++) {
      const cardIdx = (scenes[si].cards || []).findIndex(c => c.id === option.card_id);
      if (cardIdx !== -1) {
        if (si !== activeSceneIndex) setActiveSceneIndex(si);
        setActiveCardIndex(cardIdx);
        return;
      }
    }
  }, [experience, activeSceneIndex]);

  // ── Passcode screen (shown before anything else for passcode-protected links) ──
  if (passcodeRequired || linkExpired) {
    return (
      <PasscodeScreen
        onSubmit={handlePasscodeSubmit}
        error={passcodeError}
        loading={passcodeLoading}
        expired={linkExpired}
        expiredMessage={expiredMessage}
      />
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#060a14] flex items-center justify-center" data-testid="showroom-loading">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full border-2 border-[#0077FF]/20" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#0077FF] animate-spin" />
          </div>
          <p className="text-white/30 text-xs tracking-[0.2em] uppercase">{t('showroom.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-[#060a14] flex items-center justify-center px-6" data-testid="showroom-error">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-red-500/[0.07] border border-red-500/20 flex items-center justify-center mx-auto mb-5">
            <svg className="w-6 h-6 text-red-400/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-white/90 mb-2">{t('showroom.accessUnavailable')}</h1>
          <p className="text-white/40 text-sm leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  // Full Showroom Gallery: show experience picker
  if (fullShowroomGallery && !experience) {
    return (
      <div className="fixed inset-0 bg-[#060a14] overflow-y-auto" data-testid="full-showroom-gallery">
        <div className="min-h-full px-6 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-3 mb-2">
                <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-12" />
                <span className="text-2xl font-bold text-white/90 tracking-tight">{showroomName || 'Showroom'}</span>
              </div>
              <p className="text-white/30 text-sm">Choose an experience to explore</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
              {fullShowroomGallery.map((exp) => (
                <button
                  key={exp.id}
                  onClick={() => loadGalleryExperience(exp.id)}
                  data-testid={`gallery-tile-${exp.id}`}
                  className="group relative overflow-hidden rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#0077FF]/40 transition-all text-left"
                >
                  {exp.background_url ? (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={exp.background_url}
                        alt={exp.name}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#0077FF]/20 to-[#0077FF]/5 flex items-center justify-center">
                      <span className="text-white/20 text-3xl font-bold">{(exp.name || '?')[0]}</span>
                    </div>
                  )}
                  <div className="px-4 py-3">
                    <h3 className="font-semibold text-white/85 text-sm group-hover:text-white transition-colors truncate">{exp.name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-white/30">
                      {exp.vertical && <span className="uppercase tracking-wider">{exp.vertical}</span>}
                      <span>{exp.scene_count} scene{exp.scene_count !== 1 ? 's' : ''}</span>
                      <span>{exp.card_count} card{exp.card_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeScene = experience?.scenes?.[activeSceneIndex];
  const hasMultipleScenes = (experience?.scenes?.length || 0) > 1;
  const cards = activeScene?.cards || [];
  const activeCard = cards[activeCardIndex];
  const hasMultipleCards = cards.length > 1;

  // Compute synced image container height for multi-card scenes
  // When all images are loaded, find the max rendered height so all cards are identical
  const syncedImgHeight = (() => {
    if (cards.length <= 1) return null;
    const smPad = 10;
    let maxH = 0;
    for (const c of cards) {
      const s = c.card_scale || 1;
      const innerW = (240 * s) - (smPad * 2);
      const dims = cardImageDims[c.id];
      // Use recorded dims when available, otherwise fall back to 3:2 aspect ratio
      const renderH = dims ? innerW * (dims.h / dims.w) : innerW * (2 / 3);
      maxH = Math.max(maxH, renderH);
    }
    return Math.round(maxH);
  })();

  // Compute actual total card outer height for menu positioning
  const getCardOuterHeight = (card) => {
    const s = card.card_scale || 1;
    const smPad = 10;
    const footerH = 32; // "TAP TO EXPLORE" section height
    const borderH = 2; // border
    if (syncedImgHeight) {
      return syncedImgHeight + smPad * 2 + footerH + borderH;
    }
    const dims = cardImageDims[card.id];
    if (dims) {
      const innerW = (240 * s) - (smPad * 2);
      const renderH = innerW * (dims.h / dims.w);
      return Math.round(renderH) + smPad * 2 + footerH + borderH;
    }
    return null;
  };

  // Get action label — try mode_labels first, then use key itself
  const getActionLabel = (key) => {
    const ml = modeLabels[key];
    if (ml) return displayI18n(ml, lang);
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Actions for the active card
  const activeActions = Object.entries(activeCard?.destinations || {});

  return (
    <div className="fixed inset-0 bg-[#060a14] flex flex-col select-none" data-testid="showroom-page">
      <div className="relative flex-1 min-h-0 overflow-hidden">
      <style>{`
        @keyframes showroom-enter {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes showroom-enter-mobile {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes showroom-ken-burns {
          0% { transform: scale(1.0); }
          100% { transform: scale(1.06); }
        }
      `}</style>

      {activeScene?.background_url && (
        <div className={`absolute inset-0 transition-opacity ease-out ${entered && bgLoaded ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDuration: '1.5s' }}>
          <img src={activeScene.background_url} alt="" className="w-full h-full object-cover"
            style={{ animation: 'showroom-ken-burns 25s ease-in-out infinite alternate' }}
            onLoad={() => setBgLoaded(true)} draggable={false} />
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(6,10,20,0.4) 100%), linear-gradient(to top, rgba(6,10,20,0.7) 0%, rgba(6,10,20,0.15) 25%, transparent 50%, rgba(6,10,20,0.2) 100%)'
          }} />
        </div>
      )}

      {!activeScene?.background_url && (
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 40%, #0a1628 0%, #060a14 100%)' }} />
      )}

      {/* ── MOBILE: vertical card stack ── */}
      {isMobile && (
        <div className="absolute inset-0 overflow-y-auto pt-16 pb-8 px-4 flex flex-col items-center gap-8"
          data-testid="showroom-cards-container">
          {cards.map((card, i) => {
            // Action cards: fire trigger + flash, no spotlight menu
            if (card.card_type === 'action') {
              return (
                <div key={card.id} className="flex flex-col items-center gap-3 w-full" style={{ maxWidth: `${Math.round(200 * (card.card_scale || 1))}px` }}>
                  <TapCard card={card} index={i} lang={lang}
                    isActive={i === activeCardIndex}
                    onSelect={() => setActiveCardIndex(i)}
                    onTap={() => handleActionTap(card)}
                    selectedDestUrl="__action__"
                    syncedImgHeight={syncedImgHeight}
                    onImageLoad={handleCardImageLoad}
                    mobileMode
                    demoMode={demoMode} />
                </div>
              );
            }
            const selectedKey = cardModes[card.id] || Object.keys(card.destinations || {})[0] || '';
            const cardDestUrl = card.destinations?.[selectedKey] || '';
            const cardSocialConfig = socialMediaConfig.filter(sm => {
              if (sm.show_on === 'selected') return (sm.card_ids || []).includes(card.id);
              if (!sm.show_on && sm.card_id) return sm.card_id === card.id;
              return true; // 'all' or legacy unset
            });
            const smOverride = activeSocialMedia
              ? cardSocialConfig.find(sm => sm.label === activeSocialMedia && sm.external_url)
              : null;
            const selectedUrl = smOverride?.external_url || cardDestUrl;
            const actions = Object.entries(card.destinations || {});
            return (
              <div key={card.id} className="flex flex-col items-center gap-3 w-full" style={{ maxWidth: `${Math.round(200 * (card.card_scale || 1))}px` }}>
                <TapCard card={card} index={i} lang={lang}
                  isActive={i === activeCardIndex}
                  onSelect={() => setActiveCardIndex(i)}
                  onTap={(url) => recordTap(card, selectedKey, url)}
                  selectedDestUrl={selectedUrl}
                  syncedImgHeight={syncedImgHeight}
                  onImageLoad={handleCardImageLoad}
                  mobileMode
                  demoMode={demoMode} />
                {(actions.length >= 2 || cardSocialConfig.length > 0) && (
                  <CardSpotlightMenu card={card} cardIndex={i} lang={lang}
                    isActive={i === activeCardIndex}
                    onSelect={() => setActiveCardIndex(i)}
                    selectedKey={selectedKey}
                    onModeChange={(key) => setCardModes(prev => ({ ...prev, [card.id]: key }))}
                    getActionLabel={getActionLabel}
                    actualCardHeight={getCardOuterHeight(card)}
                    mobileMode
                    socialMediaConfig={cardSocialConfig}
                    onSocialMediaChange={handleSocialMediaChange}
                    activeSocialMedia={activeSocialMedia} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP: absolute positioned cards ── */}
      {!isMobile && (
        <div className="absolute inset-0" data-testid="showroom-cards-container">
          {cards.map((card, i) => {
            // Action cards: fire trigger + flash, no navigation
            if (card.card_type === 'action') {
              return (
                <TapCard key={card.id} card={card} index={i} lang={lang}
                  isActive={i === activeCardIndex}
                  onSelect={() => setActiveCardIndex(i)}
                  onTap={() => handleActionTap(card)}
                  selectedDestUrl="__action__"
                  syncedImgHeight={syncedImgHeight}
                  onImageLoad={handleCardImageLoad}
                  demoMode={demoMode} />
              );
            }
            const selectedKey = cardModes[card.id] || Object.keys(card.destinations || {})[0] || '';
            const cardDestUrl = card.destinations?.[selectedKey] || '';
            const cardSocialConfig = socialMediaConfig.filter(sm => {
              if (sm.show_on === 'selected') return (sm.card_ids || []).includes(card.id);
              if (!sm.show_on && sm.card_id) return sm.card_id === card.id;
              return true; // 'all' or legacy unset
            });
            const smOverride = activeSocialMedia
              ? cardSocialConfig.find(sm => sm.label === activeSocialMedia && sm.external_url)
              : null;
            const selectedUrl = smOverride?.external_url || cardDestUrl;
            return (
              <TapCard key={card.id} card={card} index={i} lang={lang}
                isActive={i === activeCardIndex}
                onSelect={() => setActiveCardIndex(i)}
                onTap={(url) => recordTap(card, selectedKey, url)}
                selectedDestUrl={selectedUrl}
                syncedImgHeight={syncedImgHeight}
                onImageLoad={handleCardImageLoad}
                demoMode={demoMode} />
            );
          })}
        </div>
      )}

      {/* Per-card DEMO MODE spotlight menus — desktop only (mobile menus inline above) */}
      {!isMobile && (
        <div className="absolute inset-0 pointer-events-none" data-testid="spotlight-menus-container">
          {cards.map((card, i) => {
            if (card.card_type === 'action') return null; // Action cards never have spotlight menus
            const hasDests = Object.keys(card.destinations || {}).length >= 2;
            const cardSocialConfig = socialMediaConfig.filter(sm => {
              if (sm.show_on === 'selected') return (sm.card_ids || []).includes(card.id);
              if (!sm.show_on && sm.card_id) return sm.card_id === card.id;
              return true; // 'all' or legacy unset
            });
            const hasSocial = cardSocialConfig.length > 0;
            if (!hasDests && !hasSocial) return null;
            const selectedKey = cardModes[card.id] || Object.keys(card.destinations || {})[0] || '';
            return (
              <div key={`menu-${card.id}`} className="pointer-events-auto">
                <CardSpotlightMenu card={card} cardIndex={i} lang={lang}
                  isActive={i === activeCardIndex}
                  onSelect={() => setActiveCardIndex(i)}
                  selectedKey={selectedKey}
                  onModeChange={(key) => setCardModes(prev => ({ ...prev, [card.id]: key }))}
                  getActionLabel={getActionLabel}
                  actualCardHeight={getCardOuterHeight(card)}
                  socialMediaConfig={cardSocialConfig}
                  onSocialMediaChange={handleSocialMediaChange}
                  activeSocialMedia={activeSocialMedia} />
              </div>
            );
          })}
        </div>
      )}

      {/* Back to Showroom — visible only when launched from gallery */}
      {isTheShow && new URLSearchParams(location.search).get('from') === 'showroom' && (
        <button
          onClick={() => navigate('/admin/showroom')}
          className={`absolute top-4 left-4 sm:top-6 sm:left-6 z-20 flex items-center gap-1.5
            px-3 py-1.5 rounded-lg text-[10px] font-semibold tracking-wider uppercase
            text-white/40 hover:text-white/70 border border-white/[0.06] hover:border-white/[0.15]
            transition-all duration-300 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
          style={{ background: 'rgba(8,14,28,0.7)', backdropFilter: 'blur(12px)', transitionDelay: '0.5s' }}
          data-testid="back-to-showroom-btn"
        >
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
          Showroom
        </button>
      )}

      {/* Back to Gallery — visible for full-showroom token sessions */}
      {fullShowroomGallery && (
        <button
          onClick={backToGallery}
          className={`absolute top-4 left-4 sm:top-6 sm:left-6 z-20 flex items-center gap-1.5
            px-3 py-1.5 rounded-lg text-[10px] font-semibold tracking-wider uppercase
            text-white/40 hover:text-white/70 border border-white/[0.06] hover:border-white/[0.15]
            transition-all duration-300 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
          style={{ background: 'rgba(8,14,28,0.7)', backdropFilter: 'blur(12px)', transitionDelay: '0.5s' }}
          data-testid="back-to-gallery-btn"
        >
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
          All Experiences
        </button>
      )}

      {/* Experience title */}
      <div className={`absolute top-4 left-4 sm:top-6 sm:left-6 z-10 transition-all duration-700 delay-500 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
        ${(isTheShow && new URLSearchParams(location.search).get('from') === 'showroom') || fullShowroomGallery ? 'mt-10' : ''}`}>
        <p className="text-[9px] sm:text-[10px] font-semibold tracking-[0.2em] text-white/20 uppercase">
          {displayI18n(experience?.name, lang)}
        </p>
      </div>

      {/* Preview badge + language switcher */}
      <div className={`absolute top-4 right-4 sm:top-6 sm:right-6 z-10 flex items-center gap-2 transition-all duration-700 delay-500 ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
        <div className="flex gap-0.5 rounded-lg p-0.5" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)' }} data-testid="showroom-lang-switcher">
          {SUPPORTED_LANGS.map(l => (
            <button key={l} onClick={() => changeLang(l)} data-testid={`showroom-lang-${l}`}
              className={`px-2 py-1 rounded-md text-[9px] font-bold tracking-wider transition-all ${
                lang === l ? 'bg-[#0077FF]/20 text-[#4DB8FF] border border-[#0077FF]/25' : 'text-white/30 hover:text-white/50 border border-transparent'
              }`}>
              {LANG_LABELS[l]}
            </button>
          ))}
        </div>
        {isPreview && !isDemonstrate && (
          <span className="px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[9px] font-semibold tracking-wider uppercase">
            {t('showroom.adminPreview')}
          </span>
        )}
        {isDemonstrate && (
          <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-[9px] font-semibold tracking-wider uppercase" data-testid="demonstrate-badge">
            SUPER ADMIN
          </span>
        )}
        {!isDemonstrate && timeLeft !== null && timeLeft > 0 && (
          <span className={`px-2.5 py-1 rounded-full border text-[9px] font-semibold tracking-wider uppercase font-mono ${
            timeLeft < 30000 ? 'bg-red-500/20 border-red-500/30 text-red-300' : 'bg-blue-500/20 border-blue-500/30 text-blue-300'
          }`} data-testid="session-timer">
            {Math.floor(timeLeft / 60000)}:{String(Math.floor((timeLeft % 60000) / 1000)).padStart(2, '0')}
          </span>
        )}
      </div>

      </div>{/* end stage wrapper */}

      {/* Scene navigation — structurally below the stage area */}
      {hasMultipleScenes && (
        <div className={`flex-shrink-0 flex justify-center py-2.5 bg-[#060a14] z-20 transition-all duration-700 delay-300 ${entered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
          <div className="flex gap-1 rounded-xl p-1.5 border border-white/[0.06]"
            style={{ background: 'rgba(8,14,28,0.8)', backdropFilter: 'blur(20px)' }}>
            {experience.scenes.map((scene, i) => (
              <button key={scene.id} onClick={() => { setActiveSceneIndex(i); setBgLoaded(false); setCardModes({}); }} data-testid={`scene-tab-${i}`}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap transition-all duration-200
                  ${i === activeSceneIndex ? 'bg-[#0077FF]/15 text-[#4DB8FF] border border-[#0077FF]/25' : 'text-white/30 hover:text-white/50 border border-transparent'}`}>
                {displayI18n(scene.name, lang)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* GOAL! flash overlay for Action cards */}
      {goalFlash && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          data-testid="goal-flash-overlay"
          style={{ animation: 'goalFlashIn 0.15s ease-out forwards' }}>
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at center, rgba(220,38,38,0.85) 0%, rgba(180,0,0,0.7) 40%, rgba(100,0,0,0.5) 70%, rgba(0,0,0,0.3) 100%)',
            animation: 'goalPulse 0.4s ease-in-out 2',
          }} />
          <span className="relative text-white font-black tracking-[0.15em] select-none"
            style={{
              fontSize: 'clamp(5rem, 18vw, 14rem)',
              textShadow: '0 0 80px rgba(255,60,60,0.8), 0 0 160px rgba(255,0,0,0.4), 0 4px 20px rgba(0,0,0,0.5)',
              animation: 'goalTextPop 0.3s ease-out forwards',
            }}>
            GOAL!
          </span>
          <style>{`
            @keyframes goalFlashIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes goalPulse { 0%, 100% { opacity: 0.85; } 50% { opacity: 1; } }
            @keyframes goalTextPop { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default ShowroomPage;
